import { EASINGS, PI, SIN, TLDefaultDashStyle, TLDrawShapeSegment, Vec, modulate } from "@tldraw/tldraw";
import { TLDrawShapeUtilProps } from "./DrawShapeUtil";

export interface StrokeOptions {
  /** The base size (diameter) of the stroke. */
  size?: number;
  /** The effect of pressure on the stroke's size. */
  thinning?: number;
  /** How much to soften the stroke's edges. */
  smoothing?: number;
  streamline?: number;
  /** An easing function to apply to each point's pressure. */
  easing?(pressure: number): number;
  /** Whether to simulate pressure based on velocity. */
  simulatePressure?: boolean;
  /** Cap, taper and easing for the start of the line. */
  start?: {
    cap?: boolean;
    taper?: number | boolean;
    easing?(distance: number): number;
  };
  /** Cap, taper and easing for the end of the line. */
  end?: {
    cap?: boolean;
    taper?: number | boolean;
    easing?(distance: number): number;
  };
  /** Whether to handle the points as a completed stroke. */
  last?: boolean;
}

const PEN_EASING = (t: number) => t * 0.65 + SIN((t * PI) / 2) * 0.35;

const simulatePressureSettings = (strokeWidth: number): StrokeOptions => {
  return {
    size: strokeWidth,
    thinning: 0.5,
    streamline: modulate(strokeWidth, [9, 16], [0.64, 0.74], true), // 0.62 + ((1 + strokeWidth) / 8) * 0.06,
    smoothing: 0.62,
    easing: EASINGS.easeOutSine,
    simulatePressure: true,
  };
};

const realPressureSettings = (strokeWidth: number): StrokeOptions => {
  return {
    size: 1 + strokeWidth * 1.2,
    thinning: 0.62,
    streamline: 0.62,
    smoothing: 0.62,
    simulatePressure: false,
    easing: PEN_EASING,
  };
};

const solidSettings = (strokeWidth: number): StrokeOptions => {
  return {
    size: strokeWidth,
    thinning: 0,
    streamline: modulate(strokeWidth, [9, 16], [0.64, 0.74], true), // 0.62 + ((1 + strokeWidth) / 8) * 0.06,
    smoothing: 0.62,
    simulatePressure: false,
    easing: EASINGS.linear,
  };
};

const solidRealPressureSettings = (strokeWidth: number): StrokeOptions => {
  return {
    size: strokeWidth,
    thinning: 0,
    streamline: 0.62,
    smoothing: 0.62,
    simulatePressure: false,
    easing: EASINGS.linear,
  };
};

export function getHighlightFreehandSettings({
  strokeWidth,
  showAsComplete,
}: {
  strokeWidth: number;
  showAsComplete: boolean;
}): StrokeOptions {
  return {
    size: 1 + strokeWidth,
    thinning: 0,
    streamline: 0.5,
    smoothing: 0.5,
    simulatePressure: false,
    easing: EASINGS.easeOutSine,
    last: showAsComplete,
  };
}

export function getFreehandOptions(
  shapeProps: { dash: TLDefaultDashStyle; isPen: boolean; isComplete: boolean },
  strokeWidth: number,
  forceComplete: boolean,
  forceSolid: boolean
): StrokeOptions {
  const last = shapeProps.isComplete || forceComplete;

  if (forceSolid) {
    if (shapeProps.isPen) {
      return { ...solidRealPressureSettings(strokeWidth), last };
    } else {
      return { ...solidSettings(strokeWidth), last };
    }
  }

  if (shapeProps.dash === "draw") {
    if (shapeProps.isPen) {
      return { ...realPressureSettings(strokeWidth), last };
    } else {
      return { ...simulatePressureSettings(strokeWidth), last };
    }
  }

  return { ...solidSettings(strokeWidth), last };
}

export function getPointsFromSegments(segments: TLDrawShapeSegment[]) {
  const points: Vec[] = [];

  for (const segment of segments) {
    if (segment.type === "free" || segment.points.length < 2) {
      points.push(...segment.points.map(Vec.Cast));
    } else {
      const pointsToInterpolate = Math.max(4, Math.floor(Vec.Dist(segment.points[0], segment.points[1]) / 16));
      points.push(...Vec.PointsBetween(segment.points[0], segment.points[1], pointsToInterpolate));
    }
  }

  return points;
}

export function getDrawShapeStrokeDashArray(shape: TLDrawShapeUtilProps, strokeWidth: number, dotAdjustment: number) {
  return {
    draw: "none",
    solid: `none`,
    dotted: `${dotAdjustment} ${strokeWidth * 2}`,
    dashed: `${strokeWidth * 2} ${strokeWidth * 2}`,
  }[shape.props.dash];
}
